/**
 * One `page_view` per page a visitor sees — on load, and on every in-page
 * navigation after it.
 *
 * The SDK never sent one. Until 2.22.0 that was hidden: every full page load
 * minted a session, so each page left a `session_start` row carrying its URL.
 * Now a session spans the visit, and a page someone glanced at without
 * scrolling, clicking or waiting for an engagement ping left no row at all.
 *
 * In-page navigation matters as much as loads. hamla.io is a Next.js site, and
 * its visitor from X on 4 Oct 2026 went blog post → home → pricing in one page
 * load: three pages, one `window` — so watching only the load would have
 * recorded one.
 *
 * A page is its path. A changed query string (a filter, a `?v=2`) or hash is
 * the same page; counting it again would inflate every content report.
 *
 * Detection is `popstate` (back/forward) plus a one-second check of the path,
 * not a patch of `history.pushState`: patching a global the host site and its
 * router also own is the kind of change that breaks someone else's page.
 */

/** Check the path this often for in-page navigations the router made. */
const CHECK_INTERVAL_MS = 1000;

function currentPath(): string | null {
  try {
    return window.location.pathname;
  } catch {
    return null;
  }
}

/**
 * Call `onPage(href)` now, and again whenever the path changes. Returns the
 * teardown.
 */
export function watchPageViews(
  onPage: (href: string) => void,
  intervalMs: number = CHECK_INTERVAL_MS,
): () => void {
  if (typeof window === 'undefined') return () => {};

  let lastPath = currentPath();
  onPage(window.location.href);

  const check = () => {
    const path = currentPath();
    if (path === null || path === lastPath) return;
    lastPath = path;
    onPage(window.location.href);
  };

  window.addEventListener('popstate', check);
  const timer = setInterval(check, intervalMs);

  return () => {
    window.removeEventListener('popstate', check);
    clearInterval(timer);
  };
}
